import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GlassButton, GlassInput } from '../../components/ui/GlassComponents';
import AssignmentsTab from '../../components/team/AssignmentsTab';
import LogLeaveForUserModal from '../../components/team/LogLeaveForUserModal';
import { useConfirm } from '../../components/ui/ConfirmDialog';

const Profile = () => {
  const { userId } = useParams(); // For viewing other users' profiles
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [logLeaveOpen, setLogLeaveOpen] = useState(false);
  
  // Profile being viewed (could be current user or another user)
  const [viewedUser, setViewedUser] = useState(null);
  const [isViewingOtherUser, setIsViewingOtherUser] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    department: '',
    bio: '',
    startDate: '',
    terminationDate: '',
    dateOfBirth: '',
    contractType: ''
  });

  // HR Data States
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [disciplinaries, setDisciplinaries] = useState([]);
  const [equipment, setEquipment] = useState([]);

  useEffect(() => {
    const targetUserId = userId || user?.id;
    const isOtherUser = userId && userId !== user?.id;
    
    setIsViewingOtherUser(isOtherUser);
    setCanEdit(!isOtherUser || ['team_admin', 'super_admin'].includes(user?.role));
    
    if (targetUserId) {
      fetchProfile(targetUserId);
      fetchHRData(targetUserId);
    }
  }, [userId, user]);

  const fetchProfile = async (targetUserId) => {
    setLoading(true);
    try {
      let response;
      if (targetUserId === user?.id) {
        response = await axios.get('/api/users/profile');
      } else {
        response = await axios.get(`/api/users/${targetUserId}`);
      }
      const userData = response.data.user;
      
      setViewedUser(userData);
      setProfile({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        jobTitle: userData.jobTitle || '',
        department: userData.department || '',
        bio: userData.bio || '',
        // Employment lifecycle fields — DATEONLY comes back as YYYY-MM-DD
        startDate: userData.startDate || '',
        terminationDate: userData.terminationDate || '',
        dateOfBirth: userData.dateOfBirth || '',
        contractType: userData.contractType || ''
      });
      
      if (userData.avatar) {
        let avatarUrl;
        if (userData.avatar.startsWith('http')) {
          avatarUrl = userData.avatar;
        } else if (userData.avatar.startsWith('/uploads/')) {
          avatarUrl = `${axios.defaults.baseURL}${userData.avatar}`;
        } else if (userData.avatar.includes('/uploads/')) {
          const uploadPath = userData.avatar.substring(userData.avatar.indexOf('/uploads/'));
          avatarUrl = `${axios.defaults.baseURL}${uploadPath}`;
        } else {
          avatarUrl = `${axios.defaults.baseURL}${userData.avatar}`;
        }
        setAvatarPreview(avatarUrl);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to fetch profile');
      if (userId && userId !== user?.id) {
        navigate('/dashboard/our-people');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHRData = async (targetUserId) => {
    try {
      // Fetch emergency contacts
      const contactsRes = await axios.get(`/api/hr/emergency-contacts/${targetUserId}`);
      setEmergencyContacts(contactsRes.data.contacts || []);
      
      // Fetch documents (all users can view their own, admins can view all)
      const documentsRes = await axios.get(`/api/hr/documents/${targetUserId}`);
      setDocuments(documentsRes.data.documents || []);
      
      // Fetch disciplinaries and equipment (admin only)
      if (['team_admin', 'super_admin'].includes(user?.role)) {
        const disciplinariesRes = await axios.get(`/api/hr/disciplinaries/${targetUserId}`);
        setDisciplinaries(disciplinariesRes.data.disciplinaries || []);
        
        const equipmentRes = await axios.get(`/api/hr/equipment/${targetUserId}`);
        setEquipment(equipmentRes.data.equipment || []);
      }
    } catch (error) {
      console.error('Error fetching HR data:', error);
      // Don't show error toast for HR data as it's not critical
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const formData = new FormData();
      Object.keys(profile).forEach(key => {
        formData.append(key, profile[key]);
      });
      
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      let response;
      if (isViewingOtherUser && ['team_admin', 'super_admin'].includes(user?.role)) {
        response = await axios.put(`/api/users/${viewedUser.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.put('/api/users/me', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.data.success) {
        toast.success('Profile updated successfully');
        if (!isViewingOtherUser) {
          updateProfile(response.data.user);
        }
        setViewedUser(response.data.user);
        setAvatarFile(null);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const tabs = [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'work', name: 'Work Info', icon: '💼' },
    { id: 'emergency', name: 'Emergency Contacts', icon: '🚨' },
    { id: 'documents', name: 'Documents', icon: '📄' }, // Now visible to all users
    // Admin-only tabs
    ...(canEdit && ['team_admin', 'super_admin'].includes(user?.role) ? [
      { id: 'disciplinaries', name: 'Disciplinaries', icon: '⚠️', adminOnly: true },
      { id: 'equipment', name: 'Equipment', icon: '💻', adminOnly: true }
    ] : []),
    // Assignments tab: visible to any admin, including when they're viewing
    // their own profile. A team_admin still belongs to a department and a
    // leave policy like everyone else.
    ...(['team_admin', 'super_admin'].includes(user?.role) ? [
      { id: 'assignments', name: 'Assignments', icon: '🏷️', adminOnly: true }
    ] : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                {isViewingOtherUser ? `${viewedUser?.firstName} ${viewedUser?.lastName}` : 'My Profile'}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                {isViewingOtherUser ? 'Manage team member information' : 'Manage your personal information and settings'}
              </p>
            </div>
            {isViewingOtherUser && (
              <div className="flex items-center space-x-2">
                {['team_admin', 'super_admin'].includes(user?.role) && (
                  <GlassButton
                    onClick={() => setLogLeaveOpen(true)}
                    className="flex items-center space-x-2"
                    title="Log sick leave, unpaid absence, or any retrospective leave for this user"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v8m-4-4h8m5-4a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Log Leave</span>
                  </GlassButton>
                )}
                <GlassButton
                  onClick={() => navigate('/dashboard/our-people')}
                  className="flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back to Our People</span>
                </GlassButton>
              </div>
            )}
          </div>
        </div>

        {/* Profile Header Card */}
        <GlassCard className="p-6 mb-8">
          <div className="flex items-center space-x-6">
            <div className="relative">
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
                </div>
              )}
              {canEdit && (
                <label className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 cursor-pointer shadow-lg transition-colors">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {profile.firstName} {profile.lastName}
              </h2>
              {profile.jobTitle && (
                <p className="text-blue-600 dark:text-blue-400 font-medium mb-1">
                  {profile.jobTitle}
                </p>
              )}
              {profile.department && (
                <p className="text-gray-600 dark:text-gray-300">
                  {profile.department}
                </p>
              )}
              <div className="flex items-center mt-2 space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span>📧 {profile.email}</span>
                {profile.phone && <span>📞 {profile.phone}</span>}
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white/20 dark:bg-gray-800/20 text-gray-700 dark:text-gray-300 hover:bg-white/30 dark:hover:bg-gray-800/30'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'personal' && (
            <PersonalInfoTab
              profile={profile}
              setProfile={setProfile}
              canEdit={canEdit}
              saving={saving}
              onSave={handleProfileUpdate}
            />
          )}
          
          {activeTab === 'work' && (
            <WorkInfoTab
              profile={profile}
              setProfile={setProfile}
              canEdit={canEdit}
              saving={saving}
              onSave={handleProfileUpdate}
              isAdmin={['team_admin', 'super_admin'].includes(user?.role)}
              isViewingOtherUser={isViewingOtherUser}
            />
          )}
          
          {activeTab === 'emergency' && (
            <EmergencyContactsTab
              contacts={emergencyContacts}
              setContacts={setEmergencyContacts}
              userId={viewedUser?.id}
              canEdit={canEdit}
            />
          )}
          
          {activeTab === 'documents' && (
            <DocumentsTab
              documents={documents}
              setDocuments={setDocuments}
              userId={viewedUser?.id}
              canEdit={canEdit && ['team_admin', 'super_admin'].includes(user?.role)}
              isOwnProfile={!isViewingOtherUser}
              userRole={user?.role}
            />
          )}
          
          {activeTab === 'disciplinaries' && (
            <DisciplinariesTab
              disciplinaries={disciplinaries}
              setDisciplinaries={setDisciplinaries}
              userId={viewedUser?.id}
              canEdit={canEdit && ['team_admin', 'super_admin'].includes(user?.role)}
            />
          )}
          
          {activeTab === 'equipment' && (
            <EquipmentTab
              equipment={equipment}
              setEquipment={setEquipment}
              userId={viewedUser?.id}
              canEdit={canEdit && ['team_admin', 'super_admin'].includes(user?.role)}
            />
          )}

          {activeTab === 'assignments' && (
            <AssignmentsTab userId={viewedUser?.id} />
          )}
        </div>
      </div>

      {/* Admin-only: log a leave period on this user's behalf */}
      <LogLeaveForUserModal
        open={logLeaveOpen}
        onClose={() => setLogLeaveOpen(false)}
        targetUser={viewedUser}
        onLogged={() => {
          // Nothing critical to refresh on this page; if the admin
          // also has the assignments/balance tab open we'd reload
          // there, but AssignmentsTab fetches its own data per-render.
        }}
      />
    </div>
  );
};

// Personal Info Tab Component
const PersonalInfoTab = ({ profile, setProfile, canEdit, saving, onSave }) => {
  return (
    <form onSubmit={onSave}>
      <GlassCard className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Personal Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              First Name *
            </label>
            <GlassInput
              type="text"
              value={profile.firstName}
              onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
              disabled={!canEdit}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Last Name *
            </label>
            <GlassInput
              type="text"
              value={profile.lastName}
              onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
              disabled={!canEdit}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email *
            </label>
            <GlassInput
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              disabled={!canEdit}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone
            </label>
            <GlassInput
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              disabled={!canEdit}
            />
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bio
          </label>
          <textarea
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            disabled={!canEdit}
            rows={4}
            className="w-full px-4 py-3 bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:opacity-60"
            placeholder="Tell us about yourself..."
          />
        </div>
        
        {canEdit && (
          <div className="mt-6 flex justify-end">
            <GlassButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </GlassButton>
          </div>
        )}
      </GlassCard>
    </form>
  );
};

// Work Info Tab Component
const WorkInfoTab = ({ profile, setProfile, canEdit, saving, onSave, isAdmin, isViewingOtherUser }) => {
  // Lifecycle fields are only editable by admins viewing another user
  // (team admins don't generally edit their own employment dates). For
  // their own profile we just display read-only.
  const showLifecycleSection = isViewingOtherUser && isAdmin;

  return (
    <form onSubmit={onSave}>
      <GlassCard className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Work Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Job Title
            </label>
            <GlassInput
              type="text"
              value={profile.jobTitle}
              onChange={(e) => setProfile({ ...profile, jobTitle: e.target.value })}
              disabled={!canEdit}
              placeholder="e.g. Software Engineer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Department (free text)
            </label>
            <GlassInput
              type="text"
              value={profile.department}
              onChange={(e) => setProfile({ ...profile, department: e.target.value })}
              disabled={!canEdit}
              placeholder="e.g. Engineering"
            />
          </div>
        </div>

        {showLifecycleSection && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Employment lifecycle</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Admin-only. Used for payroll exports, tenure display, and document expiry reminders.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start date
                </label>
                <input
                  type="date"
                  value={profile.startDate || ''}
                  onChange={(e) => setProfile({ ...profile, startDate: e.target.value })}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Termination date
                </label>
                <input
                  type="date"
                  value={profile.terminationDate || ''}
                  onChange={(e) => setProfile({ ...profile, terminationDate: e.target.value })}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Leave empty for active employees.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date of birth
                </label>
                <input
                  type="date"
                  value={profile.dateOfBirth || ''}
                  onChange={(e) => setProfile({ ...profile, dateOfBirth: e.target.value })}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contract type
                </label>
                <select
                  value={profile.contractType || ''}
                  onChange={(e) => setProfile({ ...profile, contractType: e.target.value })}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                >
                  <option value="">—</option>
                  <option value="permanent">Permanent</option>
                  <option value="fixed_term">Fixed term</option>
                  <option value="contractor">Contractor</option>
                  <option value="part_time">Part-time</option>
                  <option value="apprentice">Apprentice</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {canEdit && (
          <div className="mt-6 flex justify-end">
            <GlassButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </GlassButton>
          </div>
        )}
      </GlassCard>
    </form>
  );
};

// Emergency Contacts Tab Component
const EmergencyContactsTab = ({ contacts, setContacts, userId, canEdit }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    relationship: '',
    phone: '',
    email: '',
    address: '',
    isPrimary: false
  });

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!newContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post('/api/hr/emergency-contacts', {
        userId,
        ...newContact
      });
      
      if (response.data.success) {
        setContacts([...contacts, response.data.contact]);
        setNewContact({
          name: '',
          relationship: '',
          phone: '',
          email: '',
          address: '',
          isPrimary: false
        });
        setShowAddForm(false);
        toast.success('Emergency contact added successfully');
      }
    } catch (error) {
      console.error('Error adding emergency contact:', error);
      toast.error(error.response?.data?.message || 'Failed to add emergency contact');
    } finally {
      setSaving(false);
    }
  };

  const handleEditContact = async (e) => {
    e.preventDefault();
    if (!newContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`/api/hr/emergency-contacts/${editingContact.id}`, {
        userId,
        ...newContact
      });
      
      if (response.data.success) {
        setContacts(contacts.map(c => 
          c.id === editingContact.id ? response.data.contact : c
        ));
        setNewContact({
          name: '',
          relationship: '',
          phone: '',
          email: '',
          address: '',
          isPrimary: false
        });
        setEditingContact(null);
        toast.success('Emergency contact updated successfully');
      }
    } catch (error) {
      console.error('Error updating emergency contact:', error);
      toast.error(error.response?.data?.message || 'Failed to update emergency contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    const ok = await confirm({
      title: 'Delete this emergency contact?',
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) {
      return;
    }

    try {
      const response = await axios.delete(`/api/hr/emergency-contacts/${contactId}`);
      if (response.data.success) {
        setContacts(contacts.filter(c => c.id !== contactId));
        toast.success('Emergency contact deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting emergency contact:', error);
      toast.error(error.response?.data?.message || 'Failed to delete emergency contact');
    }
  };

  const startEditContact = (contact) => {
    setEditingContact(contact);
    setNewContact({
      name: contact.name || '',
      relationship: contact.relationship || '',
      phone: contact.phone || '',
      email: contact.email || '',
      address: contact.address || '',
      isPrimary: contact.isPrimary || false
    });
    setShowAddForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Emergency Contacts</h3>
        {canEdit && (
          <GlassButton onClick={() => setShowAddForm(true)}>
            Add Contact
          </GlassButton>
        )}
      </div>

      {/* Add/Edit Contact Form */}
      {showAddForm && canEdit && (
        <GlassCard className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
            </h4>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingContact(null);
                setNewContact({
                  name: '',
                  relationship: '',
                  phone: '',
                  email: '',
                  address: '',
                  isPrimary: false
                });
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={editingContact ? handleEditContact : handleAddContact}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <GlassInput
                  type="text"
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Relationship
                </label>
                <GlassInput
                  type="text"
                  value={newContact.relationship}
                  onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                  placeholder="e.g. Spouse, Parent, Sibling"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone
                </label>
                <GlassInput
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <GlassInput
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="Email address"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Address
                </label>
                <GlassInput
                  type="text"
                  value={newContact.address}
                  onChange={(e) => setNewContact({ ...newContact, address: e.target.value })}
                  placeholder="Full address"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newContact.isPrimary}
                    onChange={(e) => setNewContact({ ...newContact, isPrimary: e.target.checked })}
                    className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Primary contact</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingContact(null);
                  setNewContact({
                    name: '',
                    relationship: '',
                    phone: '',
                    email: '',
                    address: '',
                    isPrimary: false
                  });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <GlassButton type="submit" disabled={saving}>
                {saving 
                  ? (editingContact ? 'Updating...' : 'Adding...') 
                  : (editingContact ? 'Update Contact' : 'Add Contact')
                }
              </GlassButton>
            </div>
          </form>
        </GlassCard>
      )}

      {contacts.length === 0 ? (
        <GlassCard className="p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p>No emergency contacts added yet.</p>
            {canEdit && (
              <p className="text-sm mt-2">Click "Add Contact" to get started.</p>
            )}
          </div>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((contact) => (
            <GlassCard key={contact.id} className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                    {contact.name}
                    {contact.isPrimary && (
                      <span className="ml-2 px-2 py-1 bg-blue-600 text-white text-xs rounded-full">Primary</span>
                    )}
                  </h4>
                  {contact.relationship && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">{contact.relationship}</p>
                  )}
                </div>
                {canEdit && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => startEditContact(contact)}
                      className="text-blue-400 hover:text-blue-600 text-sm"
                      title="Edit contact"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteContact(contact.id)}
                      className="text-red-400 hover:text-red-600 text-sm"
                      title="Delete contact"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                {contact.phone && (
                  <div className="flex items-center">
                    <span className="mr-2">📞</span>
                    <span>{contact.phone}</span>
                  </div>
                )}
                {contact.email && (
                  <div className="flex items-center">
                    <span className="mr-2">📧</span>
                    <span>{contact.email}</span>
                  </div>
                )}
                {contact.address && (
                  <div className="flex items-start">
                    <span className="mr-2 mt-0.5">📍</span>
                    <span>{contact.address}</span>
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

// Documents Tab Component
const DOCUMENT_TYPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'right_to_work', label: 'Right to work' },
  { value: 'dbs', label: 'DBS check' },
  { value: 'driving_licence', label: 'Driving licence' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'training_cert', label: 'Training certificate' },
  { value: 'cscs_card', label: 'CSCS card' },
  { value: 'cpd', label: 'CPD record' },
  { value: 'other', label: 'Other' }
];

// Visual expiry state — green (ok), amber (expiring soon), red (expired),
// plain (no expiry set).
function getExpiryBadge(expiresAt) {
  if (!expiresAt) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiresAt);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntil = Math.floor((exp - today) / msPerDay);
  if (daysUntil < 0) return { label: 'Expired', className: 'bg-red-600 text-white' };
  if (daysUntil <= 30) return { label: `Expires in ${daysUntil}d`, className: 'bg-amber-500 text-white' };
  return { label: `Expires ${exp.toLocaleDateString()}`, className: 'bg-emerald-600 text-white' };
}

const DocumentsTab = ({ documents, setDocuments, userId, canEdit, isOwnProfile, userRole }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: '',
    isVisibleToEmployee: true,
    file: null,
    expiresAt: '',
    documentType: ''
  });

  // Edit-metadata modal state
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState({ expiresAt: '', documentType: '', title: '' });
  const [editSaving, setEditSaving] = useState(false);

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!newDocument.title.trim() || !newDocument.file) {
      toast.error('Document title and file are required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('title', newDocument.title);
      formData.append('isVisibleToEmployee', canEdit ? newDocument.isVisibleToEmployee : true);
      formData.append('file', newDocument.file);
      if (newDocument.expiresAt) formData.append('expiresAt', newDocument.expiresAt);
      if (newDocument.documentType) formData.append('documentType', newDocument.documentType);

      const response = await axios.post('/api/hr/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setDocuments([...documents, response.data.document]);
        setNewDocument({ title: '', isVisibleToEmployee: true, file: null, expiresAt: '', documentType: '' });
        setShowAddForm(false);
        toast.success('Document added successfully');
      }
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error(error.response?.data?.message || 'Failed to add document');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      expiresAt: doc.expiresAt ? String(doc.expiresAt).slice(0, 10) : '',
      documentType: doc.documentType || '',
      title: doc.title || ''
    });
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditingDoc(null);
    setEditForm({ expiresAt: '', documentType: '', title: '' });
  };

  const saveEdit = async () => {
    if (!editingDoc) return;
    setEditSaving(true);
    try {
      const resp = await axios.put(`/api/hr/documents/${editingDoc.id}`, {
        title: editForm.title,
        expiresAt: editForm.expiresAt || null,
        documentType: editForm.documentType || null
      });
      if (resp.data.success) {
        setDocuments(documents.map(d => d.id === editingDoc.id ? resp.data.document : d));
        toast.success('Document updated');
        closeEdit();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update document');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const ok = await confirm({
      title: 'Delete this document?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) {
      return;
    }

    try {
      const response = await axios.delete(`/api/hr/documents/${documentId}`);
      if (response.data.success) {
        setDocuments(documents.filter(d => d.id !== documentId));
        toast.success('Document deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`/api/hr/files/download/${fileId}`);
      if (response.data.success) {
        // Open the signed URL in a new tab for secure download
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Documents</h3>
        {(canEdit || isOwnProfile) && (
          <GlassButton onClick={() => setShowAddForm(true)}>
            Add Document
          </GlassButton>
        )}
      </div>

      {/* Add Document Form */}
      {showAddForm && (canEdit || isOwnProfile) && (
        <GlassCard className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Add Document</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={handleAddDocument}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Document Title *
                </label>
                <GlassInput
                  type="text"
                  value={newDocument.title}
                  onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                  placeholder="Document title"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload File *
                </label>
                <input
                  type="file"
                  onChange={(e) => setNewDocument({ ...newDocument, file: e.target.files[0] })}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Document type (optional)
                  </label>
                  <select
                    value={newDocument.documentType}
                    onChange={(e) => setNewDocument({ ...newDocument, documentType: e.target.value })}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  >
                    {DOCUMENT_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Expiry date (optional)
                  </label>
                  <input
                    type="date"
                    value={newDocument.expiresAt}
                    onChange={(e) => setNewDocument({ ...newDocument, expiresAt: e.target.value })}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    If set, we'll email reminders 30 / 14 / 3 days before expiry.
                  </p>
                </div>
              </div>

              {/* Visibility checkbox - only show for admins */}
              {canEdit && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newDocument.isVisibleToEmployee}
                      onChange={(e) => setNewDocument({ ...newDocument, isVisibleToEmployee: e.target.checked })}
                      className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Visible to employee</span>
                  </label>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <GlassButton type="submit" disabled={saving}>
                {saving ? 'Adding...' : 'Add Document'}
              </GlassButton>
            </div>
          </form>
        </GlassCard>
      )}

      {documents.length === 0 ? (
        <GlassCard className="p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No documents added yet.</p>
            {canEdit && (
              <p className="text-sm mt-2">Click "Add Document" to get started.</p>
            )}
          </div>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((document) => {
            const badge = getExpiryBadge(document.expiresAt);
            const typeOpt = DOCUMENT_TYPE_OPTIONS.find(o => o.value === document.documentType);
            return (
              <GlassCard key={document.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white flex flex-wrap items-center gap-2">
                      📄 {document.title}
                      {document.isVisibleToUser ? (
                        <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">Shared</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">Private</span>
                      )}
                      {badge && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Added: {new Date(document.createdAt).toLocaleDateString()}
                      {typeOpt && typeOpt.value && <> · Type: {typeOpt.label}</>}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => openEdit(document)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                        title="Edit metadata (type / expiry)"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-400 hover:text-red-600 text-sm"
                        title="Delete document"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>

                {document.filePath && (
                  <button
                    onClick={() => handleDownloadFile(document.id, document.fileName)}
                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    {document.fileName}
                  </button>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}

      {editingDoc && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit document</h3>
            </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={editForm.documentType}
                    onChange={e => setEditForm({ ...editForm, documentType: e.target.value })}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  >
                    {DOCUMENT_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expiry date</label>
                  <input
                    type="date"
                    value={editForm.expiresAt}
                    onChange={e => setEditForm({ ...editForm, expiresAt: e.target.value })}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Clearing this removes expiry tracking for this document.
                  </p>
                </div>
              </div>
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={editSaving}
                  className="py-2 px-4 border border-gray-300 dark:border-gray-500 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <GlassButton onClick={saveEdit} disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save'}
                </GlassButton>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

// Disciplinaries Tab Component
const DisciplinariesTab = ({ disciplinaries, setDisciplinaries, userId, canEdit }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newDisciplinary, setNewDisciplinary] = useState({
    date: '',
    notes: '',
    file: null
  });

  // Define handleDownloadFile directly in this component to avoid scoping issues
  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`/api/hr/files/download/${fileId}`);
      if (response.data.success) {
        // Open the signed URL in a new tab for secure download
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleAddDisciplinary = async (e) => {
    e.preventDefault();
    if (!newDisciplinary.date || !newDisciplinary.notes.trim()) {
      toast.error('Date and notes are required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('date', newDisciplinary.date);
      formData.append('notes', newDisciplinary.notes);
      if (newDisciplinary.file) {
        formData.append('file', newDisciplinary.file);
      }

      const response = await axios.post('/api/hr/disciplinaries', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setDisciplinaries([...disciplinaries, response.data.disciplinary]);
        setNewDisciplinary({ date: '', notes: '', file: null });
        setShowAddForm(false);
        toast.success('Disciplinary record added successfully');
      }
    } catch (error) {
      console.error('Error adding disciplinary:', error);
      toast.error(error.response?.data?.message || 'Failed to add disciplinary record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDisciplinary = async (disciplinaryId) => {
    const ok = await confirm({
      title: 'Delete this disciplinary record?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) {
      return;
    }

    try {
      const response = await axios.delete(`/api/hr/disciplinaries/${disciplinaryId}`);
      if (response.data.success) {
        setDisciplinaries(disciplinaries.filter(d => d.id !== disciplinaryId));
        toast.success('Disciplinary record deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting disciplinary:', error);
      toast.error(error.response?.data?.message || 'Failed to delete disciplinary record');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Disciplinaries</h3>
        {canEdit && (
          <GlassButton onClick={() => setShowAddForm(true)}>
            Add Disciplinary
          </GlassButton>
        )}
      </div>

      {/* Add Disciplinary Form */}
      {showAddForm && canEdit && (
        <GlassCard className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Add Disciplinary Record</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={handleAddDisciplinary}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date *
                </label>
                <GlassInput
                  type="date"
                  value={newDisciplinary.date}
                  onChange={(e) => setNewDisciplinary({ ...newDisciplinary, date: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes *
                </label>
                <textarea
                  value={newDisciplinary.notes}
                  onChange={(e) => setNewDisciplinary({ ...newDisciplinary, notes: e.target.value })}
                  placeholder="Disciplinary notes and details..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Supporting Document (Optional)
                </label>
                <input
                  type="file"
                  onChange={(e) => setNewDisciplinary({ ...newDisciplinary, file: e.target.files[0] })}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 dark:file:bg-red-900 dark:file:text-red-300"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <GlassButton type="submit" disabled={saving}>
                {saving ? 'Adding...' : 'Add Disciplinary'}
              </GlassButton>
            </div>
          </form>
        </GlassCard>
      )}

      {disciplinaries.length === 0 ? (
        <GlassCard className="p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p>No disciplinary records added yet.</p>
            {canEdit && (
              <p className="text-sm mt-2">Click "Add Disciplinary" to get started.</p>
            )}
          </div>
        </GlassCard>
      ) : (
        <div className="space-y-4">
          {disciplinaries.map((disciplinary) => (
            <GlassCard key={disciplinary.id} className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                    ⚠️ Disciplinary Record
                    <span className="ml-2 px-2 py-1 bg-red-600 text-white text-xs rounded-full">
                      {new Date(disciplinary.date).toLocaleDateString()}
                    </span>
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                    {disciplinary.notes}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Added: {new Date(disciplinary.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {canEdit && (
                  <button
                    onClick={() => handleDeleteDisciplinary(disciplinary.id)}
                    className="text-red-400 hover:text-red-600 text-sm"
                    title="Delete disciplinary record"
                  >
                    🗑️
                  </button>
                )}
              </div>
              
              {disciplinary.filePath && (
                <button
                  onClick={() => handleDownloadFile(disciplinary.id, disciplinary.fileName || 'Supporting Document')}
                  className="text-red-600 dark:text-red-400 hover:underline text-sm cursor-pointer"
                >
                  📎 View Supporting Document
                </button>
              )}
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

// Equipment Tab Component
const EquipmentTab = ({ equipment, setEquipment, userId, canEdit }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    itemName: '',
    dateGiven: '',
    dateReturned: '',
    notes: ''
  });

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    if (!newEquipment.itemName.trim() || !newEquipment.dateGiven) {
      toast.error('Item name and date given are required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post('/api/hr/equipment', {
        userId,
        ...newEquipment
      });
      
      if (response.data.success) {
        setEquipment([...equipment, response.data.equipment]);
        setNewEquipment({ itemName: '', dateGiven: '', dateReturned: '', notes: '' });
        setShowAddForm(false);
        toast.success('Equipment record added successfully');
      }
    } catch (error) {
      console.error('Error adding equipment:', error);
      toast.error(error.response?.data?.message || 'Failed to add equipment record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    const ok = await confirm({
      title: 'Delete this equipment record?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) {
      return;
    }

    try {
      const response = await axios.delete(`/api/hr/equipment/${equipmentId}`);
      if (response.data.success) {
        setEquipment(equipment.filter(e => e.id !== equipmentId));
        toast.success('Equipment record deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      toast.error(error.response?.data?.message || 'Failed to delete equipment record');
    }
  };

  const handleReturnEquipment = async (equipmentId) => {
    try {
      // Find the equipment record to get existing data
      const equipmentRecord = equipment.find(e => e.id === equipmentId);
      if (!equipmentRecord) {
        toast.error('Equipment record not found');
        return;
      }

      const response = await axios.put(`/api/hr/equipment/${equipmentId}`, {
        itemName: equipmentRecord.itemName,
        dateGiven: equipmentRecord.dateGiven,
        dateReturned: new Date().toISOString().split('T')[0],
        notes: equipmentRecord.notes || ''
      });
      
      if (response.data.success) {
        setEquipment(equipment.map(e => 
          e.id === equipmentId ? response.data.equipment : e
        ));
        toast.success('Equipment marked as returned');
      }
    } catch (error) {
      console.error('Error returning equipment:', error);
      toast.error(error.response?.data?.message || 'Failed to mark equipment as returned');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Equipment</h3>
        {canEdit && !showAddForm && (
          <GlassButton onClick={() => setShowAddForm(true)}>
            Add Equipment
          </GlassButton>
        )}
      </div>

      {/* Add Equipment Form */}
      {showAddForm && canEdit && (
        <GlassCard className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Add Equipment Record</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={handleAddEquipment}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Item Name *
                </label>
                <GlassInput
                  type="text"
                  value={newEquipment.itemName}
                  onChange={(e) => setNewEquipment({ ...newEquipment, itemName: e.target.value })}
                  placeholder="e.g. Laptop, Monitor, Phone"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Given *
                </label>
                <GlassInput
                  type="date"
                  value={newEquipment.dateGiven}
                  onChange={(e) => setNewEquipment({ ...newEquipment, dateGiven: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Returned (Optional)
                </label>
                <GlassInput
                  type="date"
                  value={newEquipment.dateReturned}
                  onChange={(e) => setNewEquipment({ ...newEquipment, dateReturned: e.target.value })}
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={newEquipment.notes}
                  onChange={(e) => setNewEquipment({ ...newEquipment, notes: e.target.value })}
                  placeholder="Additional notes about the equipment..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <GlassButton type="submit" disabled={saving}>
                {saving ? 'Adding...' : 'Add Equipment'}
              </GlassButton>
            </div>
          </form>
        </GlassCard>
      )}

      {equipment.length === 0 ? (
        <GlassCard className="p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p>No equipment records added yet.</p>
            {canEdit && (
              <p className="text-sm mt-2">Click "Add Equipment" to get started.</p>
            )}
          </div>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {equipment.map((item) => (
            <GlassCard key={item.id} className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center">
                  <div className="flex items-center mr-3">
                    {canEdit ? (
                      <button
                        onClick={() => !item.dateReturned && handleReturnEquipment(item.id)}
                        className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                          item.dateReturned 
                            ? 'bg-green-500 border-green-500 text-white cursor-default' 
                            : 'border-gray-400 hover:border-green-500 cursor-pointer'
                        }`}
                        title={item.dateReturned ? 'Equipment returned' : 'Mark as returned'}
                        disabled={!!item.dateReturned}
                      >
                        {item.dateReturned && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ) : (
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        item.dateReturned ? 'bg-green-500 border-green-500 text-white' : 'border-gray-400'
                      }`}>
                        {item.dateReturned && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                    💻 {item.itemName}
                    {item.dateReturned && (
                      <span className="ml-2 px-2 py-1 bg-gray-600 text-white text-xs rounded-full">Returned</span>
                    )}
                  </h4>
                </div>
                {canEdit && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDeleteEquipment(item.id)}
                      className="text-red-400 hover:text-red-600 text-sm"
                      title="Delete equipment record"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center">
                  <span className="mr-2">📅</span>
                  <span>Given: {new Date(item.dateGiven).toLocaleDateString()}</span>
                </div>
                {item.dateReturned && (
                  <div className="flex items-center">
                    <span className="mr-2">✅</span>
                    <span>Returned: {new Date(item.dateReturned).toLocaleDateString()}</span>
                  </div>
                )}
                {item.notes && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.notes}</p>
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default Profile;
